import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Check } from "lucide-react";
import { fetchSuspiciousEvents, ackSuspicious } from "@admin/services/adminApi";
import { DataTable, Pill } from "@admin/components/Primitives";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Paginator, PAGE_SIZE } from "@admin/components/security/Paginator";
import { AdminPicker, ALL_ADMINS, adminParam } from "@admin/components/security/AdminPicker";
import { fmtDt } from "@admin/components/security/format";

export function SuspiciousSection({ canManageAdmins, meId, onChanged }) {
  const [state, setState] = useState("false");
  const [admin, setAdmin] = useState(ALL_ADMINS);
  const [skip, setSkip] = useState(0);
  const [data, setData] = useState({ total: 0, unread: 0, items: [] });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const d = await fetchSuspiciousEvents({
        acknowledged: state === "all" ? undefined : state,
        user_id: canManageAdmins ? adminParam(admin, meId) : undefined,
        skip, limit: PAGE_SIZE,
      });
      setData(d);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not load suspicious events"); }
    setBusy(false);
  }, [state, admin, skip, canManageAdmins, meId]);
  useEffect(() => { load(); }, [load]);

  const onAck = async (eid) => {
    try { await ackSuspicious(eid); toast.success("Marked as reviewed"); await load(); onChanged?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const columns = [
    { key: "created_at", label: "When", render: (r) => <span className="text-xs text-slate-600">{fmtDt(r.created_at)}</span> },
    { key: "email", label: "Admin", render: (r) => <span className="font-bold text-sm">{r.email}</span> },
    { key: "ip", label: "IP", render: (r) => <code className="text-xs">{r.ip}</code> },
    { key: "reason", label: "Reason", render: (r) => <Pill color="rose">{r.reason}</Pill> },
    {
      key: "acknowledged", label: "State",
      render: (r) => r.acknowledged
        ? <Pill color="emerald"><Check size={10} /> Reviewed</Pill>
        : <Pill color="amber">Unread</Pill>,
    },
    {
      key: "actions", label: "",
      render: (r) => !r.acknowledged && (
        <button onClick={() => onAck(r.id)} className="px-2 py-1 text-[11px] font-bold rounded-md border border-slate-200 hover:border-[#0A2540]" data-testid={`ack-${r.id}`}>
          Mark reviewed
        </button>
      ),
    },
  ];

  return (
    <section data-testid="security-suspicious-section">
      <div className="flex flex-wrap justify-between items-end gap-2 mb-3">
        <h2 className="text-xs uppercase tracking-widest font-bold text-slate-400">Suspicious sign-ins</h2>
        <div className="flex flex-wrap gap-2">
          {canManageAdmins && <AdminPicker value={admin} meId={meId} testid="suspicious-filter-admin" onChange={(v) => { setAdmin(v); setSkip(0); }} />}
          <Select value={state} onValueChange={(v) => { setState(v); setSkip(0); }}>
            <SelectTrigger className="w-[160px] h-9 text-xs" data-testid="suspicious-filter-state"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="false" data-testid="suspicious-state-unread">Unread</SelectItem>
              <SelectItem value="true" data-testid="suspicious-state-reviewed">Reviewed</SelectItem>
              <SelectItem value="all" data-testid="suspicious-state-all">All</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <DataTable testid="suspicious-table" columns={columns} rows={data.items}
        empty={busy ? "Loading…" : state === "false" ? "No unread suspicious events" : "No suspicious events"} />
      <Paginator skip={skip} total={data.total} onChange={setSkip} testid="suspicious-pager" busy={busy} />
    </section>
  );
}
